import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import startCase from "lodash/startCase";
import { METRICS, DEFAULT_VISUAL_CONFIG } from "./constants";
import LoadingScreen from "../LoadingScreen";
import { GET_IMAGE } from "../../helpers/images";
import { GET_CONNECTION_URL } from "../../api/integration";
import { GET_ZAPIER_AUTH_URL } from "../../api/integrations/zapier";
import { UseCompanyId } from "../../hooks/auth";
import { configureIntegration } from "../../redux/kpi/actions";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

const sessionConnectedApps = new Set();

function sparklinePlaceholder(count = 10, base = 100) {
  return Array.from({ length: count }, (_, i) => ({
    timestamp: `${i + 1}`,
    value: Math.round(base * (0.7 + 0.3 * Math.sin(i * 0.5))),
  }));
}

/** Map real integration metric to dashboard metric shape for display and onAdd. Ensures integrationId is set from app when metric lacks it (e.g. manual models). */
function realMetricToDisplay(metric, appCode, app) {
  const id = metric.kpiModelId || `${metric.code ?? appCode}|${metric.sourceId ?? metric.id ?? ""}`;
  const integrationId = metric.integrationId ?? app?._id ?? null;
  const rawLabel = metric.name || metric.type || "Metric";
  return {
    id,
    // Catalog types often use snake_case (hours_worked); match WorkspaceKpi MetricItem.
    name: startCase(rawLabel),
    description: metric.description || "",
    unit: "number",
    currentValue: 0,
    trend: 0,
    sparklineData: sparklinePlaceholder(),
    appId: appCode,
    visualConfig: { ...DEFAULT_VISUAL_CONFIG },
    integrationId,
    kpiModelId: metric.kpiModelId,
    isDraft: true,
    isSaved: true,
    _source: "integration",
    _integration: {
      sourceId: metric.sourceId,
      integrationId,
      kpiModelId: metric.kpiModelId,
      type: metric.type,
      isManual: metric.isManual,
      isZapier: metric.isZapier,
    },
  };
}

/** Find existing company KPI for this app metric (same matching as WorkspaceKpi MetricItem / MetricsModal). */
function findExistingCompanyKpi(displayMetric, companyKpis) {
  if (!Array.isArray(companyKpis) || !displayMetric._integration) return null;
  return companyKpis.find((k) => {
    if (k.disabled) return false;
    if (k.kpiModelId && displayMetric._integration?.kpiModelId && k.kpiModelId === displayMetric._integration.kpiModelId) return true;
    return k.sourceId === displayMetric._integration?.sourceId && k.source === displayMetric.appId;
  }) || null;
}

/** Small area chart that fills its container at any height (anchors to the bottom edge),
 * so the graph panel on each metric card never leaves a floating gap. */
function CardGraph({ data }) {
  const vals = (Array.isArray(data) ? data : []).map((d) => Number(d?.value)).filter((v) => Number.isFinite(v));
  const pts = vals.length >= 2 ? vals : [1, 1.3, 1.1, 1.5, 1.35, 1.65];
  const min = Math.min(...pts);
  const span = (Math.max(...pts) - min) || 1;
  const n = pts.length;
  const xy = pts.map((v, i) => [(i / (n - 1)) * 100, 78 - ((v - min) / span) * 52]);
  const line = xy.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden>
      <path d={`${line} L100 100 L0 100 Z`} fill="var(--gmb-primary)" fillOpacity="0.1" />
      <path d={line} fill="none" stroke="var(--gmb-primary)" strokeWidth="2" strokeOpacity="0.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MetricModal({ app, onClose, onAdd, companyKpis, onRemoveMetric }) {
  const companyId = UseCompanyId();
  const dispatch = useDispatch();
  const appId = app?.id ?? app?.code;
  const rawLogo = app?.logo ?? app?.icon;
  const appLogo = rawLogo ? GET_IMAGE(rawLogo) : null;
  const appName = app?.name ?? "App";

  const isRealApp = Array.isArray(app?.metrics) && app.metrics.length > 0;
  const isConnectedFromApi = app?.isConnected === true;
  const hasParameters = Array.isArray(app?.parameters) && app.parameters.length > 0;

  const [confirmingMetric, setConfirmingMetric] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(null);
  const [isConnected, setIsConnected] = useState(
    isConnectedFromApi || sessionConnectedApps.has(appId)
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSuccessfullyAdded, setIsSuccessfullyAdded] = useState(false);
  const [showConnectionSettings, setShowConnectionSettings] = useState(false);
  const [connectionParams, setConnectionParams] = useState({});

  // Sync connection state when the opened app changes (e.g. user selects a different app)
  useEffect(() => {
    if (!app) return;
    const connected = isConnectedFromApi || sessionConnectedApps.has(appId);
    setIsConnected(connected);
    setIsConnecting(false);
    setIsSuccessfullyAdded(false);
    setConfirmingMetric(null);
    setConfirmingRemove(null);
    setShowConnectionSettings(false);
    setConnectionParams({});
  }, [app, appId, isConnectedFromApi]);

  const appMetrics = useMemo(() => {
    if (isRealApp) {
      const code = app?.code ?? appId;
      return app.metrics.map((m) => realMetricToDisplay(m, code, app));
    }
    return METRICS.filter((m) => m.appId === appId);
  }, [isRealApp, appId, app]);

  useLockBodyScroll(!!app);

  if (!app) return null;

  /** Treat API connection as source of truth: no connect screen if app is already connected. */
  const effectiveConnected = isConnected || isConnectedFromApi;

  /** Same connect logic as WorkspaceKpi MetricsModal: OAuth/Apideck/Zapier redirect, or show settings for manual/webhook. */
  const handleConnect = () => {
    if (effectiveConnected) return;
    if (app?.isOauth || app?.isApideck) {
      const connecting = toast.loading("Initializing connection ...");
      const params = app.isApideck
        ? { unifiedApi: app.type, serviceId: app.serviceId }
        : null;

      GET_CONNECTION_URL(app.code, params)
        .then(({ data: url }) => {
          if (url) {
            toast.success("Connecting...", { id: connecting });
            const windowRef = window.open("", "_self");
            windowRef.location.href = url;
          } else {
            toast.error("Unable to establish connection.", { id: connecting });
          }
        })
        .catch((err) => {
          toast.error(err.response?.data ?? "Server error", { id: connecting });
        });
    } else if (app?.isZapier) {
      if (!companyId) {
        toast.error("Company context is required to connect Zapier.");
        return;
      }
      window.location.href = GET_ZAPIER_AUTH_URL(companyId);
    } else if (hasParameters) {
      // Manual / webhook: show connection settings form (same function as WorkspaceKpi AppSettings)
      setShowConnectionSettings(true);
    } else {
      setIsConnecting(true);
      setTimeout(() => {
        sessionConnectedApps.add(appId);
        setIsConnected(true);
        setIsConnecting(false);
      }, 1500);
    }
  };

  const handleConnectionSettingsSubmit = (e) => {
    e.preventDefault();
    const parameters = app?.parameters ?? [];
    let valid = true;
    parameters.forEach((p) => {
      if (!connectionParams[p]) {
        toast.error(`${startCase(p)} has not been defined.`);
        valid = false;
      }
    });
    if (!valid || !app?._id) return;
    dispatch(
      configureIntegration({
        integrationId: app._id,
        code: app.code,
        parameters: connectionParams,
      })
    );
    sessionConnectedApps.add(appId);
    setShowConnectionSettings(false);
    setConnectionParams({});
    setIsConnected(true);
  };

  const handleConfirm = () => {
    if (confirmingMetric) {
      const payload = confirmingMetric.visualConfig
        ? confirmingMetric
        : { ...confirmingMetric, visualConfig: { ...DEFAULT_VISUAL_CONFIG } };
      onAdd(payload);
      setConfirmingMetric(null);
      setIsSuccessfullyAdded(true);
    }
  };

  const handleConfirmRemove = () => {
    if (confirmingRemove?.existingKpi?._id && onRemoveMetric) {
      onRemoveMetric(confirmingRemove.existingKpi._id);
      setConfirmingRemove(null);
    }
  };

  const renderContent = () => {
    if (isSuccessfullyAdded) {
      return (
        <div className="flex flex-col items-center justify-center text-center px-8 py-10">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-5 border border-emerald-100">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden>
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Metric added</h2>
          <p className="text-gray-500 text-sm mb-10 max-w-[280px]">This metric is now on your dashboard.</p>
          <div className="flex flex-col w-full space-y-3">
            <button type="button" onClick={onClose} className="w-full py-3.5 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm shadow-sm hover:bg-[var(--gmb-primary-hover)] transition-all">
              View metric
            </button>
            <button
              type="button"
              onClick={() => {
                setIsSuccessfullyAdded(false);
                setConfirmingMetric(null);
              }}
              className="w-full py-3.5 bg-gray-50 text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-100 transition-all"
            >
              Add another metric
            </button>
          </div>
        </div>
      );
    }

    if (isConnecting) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8 py-12 gap-3">
          <LoadingScreen height="auto" logoHeight="160px" />
          <p className="text-gray-400 font-medium text-sm tracking-wide">{`Connecting your ${appName} account...`}</p>
        </div>
      );
    }

    // When app is already connected, show metrics instantly and skip the connect screen
    if (effectiveConnected) {
      return (
        <>
          <header className="px-5 py-3.5 border-b border-[rgba(15,23,42,0.08)] flex items-center justify-between bg-white/55 backdrop-blur-md sticky top-0 z-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm border border-gray-100 bg-white overflow-hidden p-1.5 relative">
                {appLogo ? (
                  <img src={appLogo} alt={appName} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("hidden"); }} />
                ) : null}
                <span className={`absolute inset-0 flex items-center justify-center text-base font-medium text-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] rounded-lg ${appLogo ? "hidden" : ""}`} aria-hidden>{(appName || "A").charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">{appName} metrics</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                  <p className="text-gray-400 text-[11px] font-medium">Account connected</p>
                </div>
              </div>
            </div>
            <button type="button" onClick={onClose} className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors" aria-label="Close modal">
              <span className="sr-only">Close modal</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="flex-grow overflow-y-auto px-4 py-4">
            {appMetrics.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-lg flex items-center justify-center mb-4 border border-gray-100">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden>
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm font-medium">No metrics found for this integration yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 items-start">
                {appMetrics.map((metric) => {
                  const existingKpi = findExistingCompanyKpi(metric, companyKpis);
                  const isMetricExists = !!existingKpi;
                  const handleRowClick = () => {
                    if (isMetricExists) {
                      setConfirmingRemove({ metric, existingKpi });
                    } else {
                      setConfirmingMetric(metric);
                    }
                  };
                  return (
                    <button
                      key={metric.id}
                      type="button"
                      onClick={handleRowClick}
                      aria-label={isMetricExists ? "Remove metric" : "Add metric"}
                      className="group bg-white/70 border border-gray-100 rounded-xl overflow-hidden text-left hover:border-[var(--gmb-primary-tint)] hover:shadow-md transition-colors flex items-stretch"
                    >
                      <div className="min-w-0 flex-1 py-3 px-3.5 flex flex-col justify-center">
                        <h4 className="text-[13px] font-medium text-gray-900 capitalize truncate">{metric.name}</h4>
                        <p className="text-gray-400 text-[11.5px] leading-snug mt-0.5 line-clamp-2">
                          {metric.description || "No description available for this metric."}
                        </p>
                        <div className="mt-2.5">
                          {isMetricExists ? (
                            <span aria-hidden className="inline-flex px-3 py-0.5 rounded-full text-[11px] font-medium text-pink-500 border border-pink-100 bg-pink-50/60">
                              Remove
                            </span>
                          ) : (
                            <span aria-hidden className="inline-flex px-3 py-0.5 rounded-full text-[11px] font-medium text-[var(--gmb-primary)] border border-[var(--gmb-primary-tint)] bg-[var(--gmb-primary-soft)]">
                              Add metric
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="relative shrink-0 w-24 border-l border-[rgba(15,23,42,0.06)]" aria-hidden>
                        <CardGraph data={metric.sparklineData} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {confirmingMetric && (
            <div className="absolute inset-0 z-50 flex items-center justify-center px-10 bg-slate-900/10 backdrop-blur-[3px]">
              <div
                className="rounded-lg w-full max-w-[400px] p-8 text-center"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.78) 100%)",
                  backdropFilter: "blur(18px) saturate(160%)",
                  WebkitBackdropFilter: "blur(18px) saturate(160%)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  boxShadow: "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
                }}
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mx-auto mb-5 shadow-sm border border-gray-100 overflow-hidden p-2 relative">
                  {appLogo ? (
                    <img src={appLogo} alt={appName} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("hidden"); }} />
                  ) : null}
                  <span className={`absolute inset-0 flex items-center justify-center text-base font-medium text-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] rounded-lg ${appLogo ? "hidden" : ""}`} aria-hidden>{(appName || "A").charAt(0).toUpperCase()}</span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">Add metric?</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  You&apos;re about to add <strong className="text-gray-900 font-medium">{confirmingMetric.name}</strong> to your dashboard.
                </p>
                <div className="flex flex-col space-y-3">
                  <button type="button" onClick={handleConfirm} className="w-full py-3 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm shadow-sm hover:brightness-95 transition-all">
                    Confirm and add
                  </button>
                  <button type="button" onClick={() => setConfirmingMetric(null)} className="w-full py-3 rounded-lg font-medium text-sm text-gray-400 hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {confirmingRemove && (
            <div className="absolute inset-0 z-50 flex items-center justify-center px-10 bg-slate-900/10 backdrop-blur-[3px]">
              <div
                className="rounded-lg w-full max-w-[400px] p-8 text-center"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.78) 100%)",
                  backdropFilter: "blur(18px) saturate(160%)",
                  WebkitBackdropFilter: "blur(18px) saturate(160%)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  boxShadow: "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
                }}
              >
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center mx-auto mb-5 border border-amber-100 text-amber-600">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden>
                    <path d="M12 4L2.5 20h19z" />
                    <path d="M12 10v4" />
                    <path d="M12 17.5v.01" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">Remove metric?</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  Are you sure you want to remove <strong className="text-gray-900 font-medium">{confirmingRemove.metric?.name}</strong> from your dashboard? Progress tracking for this metric will stop.
                </p>
                <div className="flex flex-col space-y-3">
                  <button type="button" onClick={handleConfirmRemove} className="w-full py-3 bg-rose-600 text-white rounded-lg font-medium text-sm hover:bg-rose-700 transition-all">
                    Remove metric
                  </button>
                  <button type="button" onClick={() => setConfirmingRemove(null)} className="w-full py-3 rounded-lg font-medium text-sm text-gray-400 hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <footer className="px-5 py-3 bg-[rgba(15,23,42,0.03)] border-t border-[rgba(15,23,42,0.08)] flex items-center justify-between mt-auto">
            <p className="text-xs font-medium text-gray-400">{appMetrics.length} metric{appMetrics.length === 1 ? "" : "s"} available</p>
            <button type="button" onClick={onClose} className="px-5 py-1.5 bg-white/70 border border-gray-200 rounded-lg font-medium text-sm text-gray-500 hover:bg-white transition-colors">
              Close
            </button>
          </footer>
        </>
      );
    }

    // Not connected: show connect screen or connection settings form
    if (!effectiveConnected) {
      // Connection settings form for manual/webhook (same function as WorkspaceKpi AppSettings, new UI)
      if (showConnectionSettings && hasParameters) {
        return (
          <div className="flex flex-col px-8 py-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center shadow-sm border border-gray-100 bg-white overflow-hidden p-2 relative flex-shrink-0">
                {appLogo ? (
                  <img src={appLogo} alt={appName} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("hidden"); }} />
                ) : null}
                <span className={`absolute inset-0 flex items-center justify-center text-base font-medium text-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] rounded-lg ${appLogo ? "hidden" : ""}`} aria-hidden>{(appName || "A").charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 tracking-tight">Connect your account</h3>
                <p className="text-gray-500 text-sm mt-0.5">Enter your {appName} credentials below.</p>
              </div>
            </div>
            <form onSubmit={handleConnectionSettingsSubmit} className="flex flex-col gap-5">
              {(app?.parameters ?? []).map((p) => (
                <div key={p}>
                  <label htmlFor={`param-${p}`} className="block text-sm font-medium text-gray-700 mb-1.5">
                    {startCase(p)}
                  </label>
                  <input
                    id={`param-${p}`}
                    type="text"
                    value={connectionParams[p] ?? ""}
                    onChange={(e) => setConnectionParams((prev) => ({ ...prev, [p]: e.target.value }))}
                    placeholder={`Enter ${startCase(p)}`}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--gmb-primary)] focus:border-transparent transition-all"
                  />
                </div>
              ))}
              <div className="flex flex-col gap-3 mt-2">
                <button
                  type="submit"
                  className="w-full py-3.5 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm shadow-sm transition-all"
                >
                  Connect
                </button>
                <button
                  type="button"
                  onClick={() => { setShowConnectionSettings(false); setConnectionParams({}); }}
                  className="w-full py-3 rounded-lg font-medium text-sm text-gray-400 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center justify-center text-center px-8 py-10">
          <div className="w-20 h-20 rounded-xl flex items-center justify-center mb-8 shadow-lg border border-gray-100 bg-white overflow-hidden p-3.5 relative">
            {appLogo ? (
              <img src={appLogo} alt={appName} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("hidden"); }} />
            ) : null}
            <span className={`absolute inset-0 flex items-center justify-center text-xl font-medium text-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] rounded-xl ${appLogo ? "hidden" : ""}`} aria-hidden>{(appName || "A").charAt(0).toUpperCase()}</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-3">Connect {appName}</h3>
          <p className="text-gray-500 font-medium leading-relaxed max-w-[280px] mb-10 text-sm">
            You must connect an account before you can browse and add metrics from this integration.
          </p>
          <button
            type="button"
            onClick={handleConnect}
            className="w-full py-3.5 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm shadow-sm transition-all flex items-center justify-center gap-3"
          >
            Connect your {appName} account
          </button>
          <button type="button" onClick={onClose} className="mt-6 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors">
            Cancel
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-8">
      <div className="absolute inset-0 bg-slate-900/15 backdrop-blur-[3px]" onClick={onClose} />
      <div
        className={`rounded-lg w-full relative z-10 flex flex-col overflow-hidden ${
          (!isConnected && !isConnecting) || isSuccessfullyAdded ? "max-w-md" : "max-w-[600px] max-h-[82vh]"
        }`}
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.62) 100%)",
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
}

export default MetricModal;
